import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Cart } from '../types';
import api from '../services/api';
import { captureUtmFromCurrentUrl, getBestUtmSourceForSession } from '../utils/utmAttribution';

const EMPTY_CART: Cart = {
  items: [],
  subtotal: 0,
  shippingCost: 0,
  discountTotal: 0,
  total: 0,
};

interface CartContextType {
  cart: Cart | null;
  loading: boolean;
  itemCount: number;
  addToCart: (productId: string, variantId?: string, quantity?: number) => Promise<void>;
  updateCartItem: (itemId: string, quantity?: number, variantId?: string) => Promise<void>;
  removeFromCart: (itemId: string) => Promise<void>;
  refreshCart: () => Promise<void>;
  clearClientCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(true);

  const createNewSession = async (): Promise<boolean> => {
    try {
      // Generate a simple browser fingerprint (in production, use a library)
      const fingerprint = localStorage.getItem('browserFingerprint') || 
        `fp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('browserFingerprint', fingerprint);

      captureUtmFromCurrentUrl();

      const referrer = typeof document !== 'undefined' ? document.referrer || '' : '';
      const utmSource = getBestUtmSourceForSession();
      const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
      const landingPath = typeof window !== 'undefined' ? window.location.pathname || '' : '';

      // Call guest identify endpoint to get session (referrer/utm/userAgent for traffic source; landingPath to exclude /admin from counts)
      const response = await api.post('/auth/guest/identify', {
        phone: null,
        email: null,
        browserFingerprint: fingerprint,
        referrer: referrer || undefined,
        utmSource,
        userAgent: userAgent || undefined,
        landingPath: landingPath || undefined
      });
      
      if (response.data.userId) {
        localStorage.setItem('userId', response.data.userId);
      }
      if (response.data.sessionId) {
        localStorage.setItem('sessionId', response.data.sessionId);
      }
      
      return true;
    } catch (error) {
      console.error('Failed to create new session:', error);
      return false;
    }
  };

  const clearClientCart = () => {
    setCart(EMPTY_CART);
    try {
      localStorage.setItem('cart', JSON.stringify(EMPTY_CART));
    } catch {
      /* ignore quota / private mode */
    }
  };

  const refreshCart = async () => {
    try {
      const response = await api.get<Cart>('/cart');
      if (response.data) {
        const data = response.data;
        const normalized: Cart = {
          items: Array.isArray(data.items) ? data.items : [],
          subtotal: data.subtotal ?? 0,
          shippingCost: data.shippingCost ?? 0,
          discountTotal: data.discountTotal ?? 0,
          total: data.total ?? 0,
        };
        setCart(normalized);
        try {
          localStorage.setItem('cart', JSON.stringify(normalized));
        } catch {
          /* ignore */
        }
      }
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      // Don't clear cart on error, keep existing state
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Don't initialize cart on admin pages
    if (window.location.pathname.startsWith('/admin')) {
      setLoading(false);
      return;
    }

    // Initialize session if not exists
    const initializeSession = async () => {
      const existingSessionId = localStorage.getItem('sessionId');
      const existingUserId = localStorage.getItem('userId');
      
      // If we already have a session or user ID, verify it works
      if (existingSessionId || existingUserId) {
        try {
          // Try to fetch cart to verify session is valid
          await refreshCart();
          return;
        } catch (error: any) {
          // If session is invalid, clear it and create new one
          console.warn('Existing session invalid, creating new session:', error);
          localStorage.removeItem('sessionId');
          localStorage.removeItem('userId');
        }
      }
      
      // Create anonymous session for guest users
      const sessionCreated = await createNewSession();
      
      if (sessionCreated) {
        // Now refresh cart with new session
        await refreshCart();
      } else {
        // Failed to create session, still try to load cart
        setLoading(false);
      }
    };
    
    // Load from localStorage first
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (e) {
        console.error('Failed to parse saved cart', e);
      }
    }
    
    initializeSession();
  }, []);

  const addToCart = async (productId: string, variantId?: string, quantity: number = 1) => {
    try {
      await api.post('/cart/items', { productId, variantId, quantity });
      await refreshCart();
    } catch (error: any) {
      console.error('Failed to add to cart:', error);
      
      // Check if error is session-related
      const isSessionError = error?.response?.data?.requiresReidentify || 
                            error?.response?.data?.error?.includes('session') ||
                            error?.response?.data?.error?.includes('Session');
      
      if (isSessionError) {
        console.log('Session error detected, attempting to re-establish session...');
        
        // Clear invalid session
        localStorage.removeItem('sessionId');
        localStorage.removeItem('userId');
        
        // Create new session
        const sessionCreated = await createNewSession();
        
        if (sessionCreated) {
          // Retry the add to cart operation
          try {
            await api.post('/cart/items', { productId, variantId, quantity });
            await refreshCart();
            return; // Success after retry
          } catch (retryError) {
            console.error('Failed to add to cart after session refresh:', retryError);
            throw retryError;
          }
        }
      }
      
      throw error;
    }
  };

  const updateCartItem = async (itemId: string, quantity?: number, variantId?: string) => {
    try {
      const payload: any = {};
      if (quantity !== undefined) payload.quantity = quantity;
      if (variantId !== undefined) payload.variantId = variantId;
      
      await api.put(`/cart/items/${itemId}`, payload);
      await refreshCart();
    } catch (error) {
      console.error('Failed to update cart item:', error);
      throw error;
    }
  };

  const removeFromCart = async (itemId: string) => {
    try {
      await api.delete(`/cart/items/${itemId}`);
      await refreshCart();
    } catch (error) {
      console.error('Failed to remove from cart:', error);
      throw error;
    }
  };

  const itemCount = cart?.items.reduce((sum, item) => sum + item.quantity, 0) || 0;

  return (
    <CartContext.Provider value={{ cart, loading, itemCount, addToCart, updateCartItem, removeFromCart, refreshCart, clearClientCart }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

